/**
 * Console API istemcisi. Oturum, HttpOnly çerezde tutulur (Spring Session). CSRF, Spring Security'nin SPA modundadır:
 * sunucu `XSRF-TOKEN` çerezini yazar, değiştiren her istek bunu `X-XSRF-TOKEN` başlığında geri gönderir.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    detail: string,
  ) {
    super(detail);
    this.name = "ApiError";
  }
}

export type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

let fetchImpl: Fetch = (input, init) => fetch(input, init);

/** Testler için. */
export function setFetch(impl: Fetch): void {
  fetchImpl = impl;
}

function csrfToken(): string | null {
  const match = document.cookie.split("; ").find((c) => c.startsWith("XSRF-TOKEN="));
  return match ? decodeURIComponent(match.slice("XSRF-TOKEN=".length)) : null;
}

export async function request<T>(method: string, path: string, body?: unknown,
  extraHeaders?: Record<string, string>): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json", ...extraHeaders };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (method !== "GET") {
    const token = csrfToken();
    if (token) {
      headers["X-XSRF-TOKEN"] = token;
    }
  }
  const res = await fetchImpl(path, {
    method,
    headers,
    credentials: "same-origin",
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 204) {
    return undefined as T;
  }
  const text = await res.text();
  const data: unknown = text ? safeJson(text) : null;
  if (!res.ok) {
    const problem = (data ?? {}) as { code?: string; status?: string; detail?: string };
    throw new ApiError(res.status, problem.code ?? problem.status ?? `HTTP_${res.status}`,
      problem.detail ?? "İstek başarısız");
  }
  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** Dosya indirme (ör. ilgili kişi dışa aktarması); cevap JSON olarak çözülmez. */
export async function download(path: string): Promise<Blob> {
  const res = await fetchImpl(path, { method: "GET", credentials: "same-origin", headers: { Accept: "application/json" } });
  if (!res.ok) {
    const problem = safeJson(await res.text()) as { code?: string; detail?: string } | null;
    throw new ApiError(res.status, problem?.code ?? `HTTP_${res.status}`, problem?.detail ?? "İndirme başarısız");
  }
  return res.blob();
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>("POST", path, body ?? {}, headers),
  put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
  delete: (path: string) => request<unknown>("DELETE", path),
};

/** Kullanıcıya gösterilecek hata metni; sunucu ayrıntısı yalnızca bilinen durumlarda kullanılır. */
export function errorText(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 403) {
      return "Bu işlem için yetkiniz yok.";
    }
    if (error.status === 401) {
      return "Oturumunuz sona erdi; lütfen yeniden giriş yapın.";
    }
    if (error.status === 400 || error.status === 409) {
      return error.message;
    }
  }
  return "İşlem tamamlanamadı; lütfen tekrar deneyin.";
}
